import { cn } from "@/lib/utils";
import {
  playStyleCategoryNamesSelector,
  playStylesSelector,
} from "@/redux/players/playersSlice";
import type { SectionTier } from "@repo/shared/hooks";
import { useTranslation } from "@repo/shared/i18n";
import { comparePlayerKey } from "@repo/shared/redux";
import { DetailedPlayer } from "@repo/shared/types";
import {
  OUTFIELD_CATEGORIES,
  type PlayStyleEntry as PlayStyle,
  type PsCategory,
  anyGoalkeeper,
  ownedStyles,
  playStyleState,
} from "@repo/shared/utils";
import { useSelector } from "react-redux";

import {
  CompareCompactRow,
  CompareGroupedBlock,
  ComparePinnedRowHeader,
  CompareRow,
  CompareSectionShell,
} from "./CompareSectionShell";
import { Mode } from "./ModeToggle";
import { PlayStyleIcon } from "./playStyleCompareUtils";

const ListMode = ({
  categories,
  playStylesAsList,
  players,
  gridStyle,
  pinnedWidth,
}: {
  categories: PsCategory[];
  playStylesAsList: PlayStyle[];
  players: DetailedPlayer[];
  gridStyle: React.CSSProperties;
  pinnedWidth?: number;
}) => {
  const categoryNames = useSelector(playStyleCategoryNamesSelector);
  return (
    <>
      {categories.map((category) => {
        const styles = ownedStyles(playStylesAsList, category, players);
        if (styles.length === 0) return null;
        return (
          <div key={category}>
            <ComparePinnedRowHeader
              label={categoryNames[category] ?? category}
              pinnedWidth={pinnedWidth}
            />
            {styles.map((style, rowIndex) => (
              <CompareRow key={style.id} index={rowIndex} gridStyle={gridStyle}>
                {players.map((player) => {
                  const { hasPlus, hasNormal } = playStyleState(
                    player,
                    style.id,
                  );
                  const owned = hasPlus || hasNormal;
                  return (
                    <div
                      key={comparePlayerKey(player)}
                      className="flex items-center gap-[7px] pl-[30px]"
                    >
                      <PlayStyleIcon
                        id={style.id}
                        hasPlus={hasPlus}
                        hasNormal={hasNormal}
                      />
                      <span
                        className={cn(
                          "text-xs font-medium",
                          owned ? "text-neutral-200" : "text-neutral-600",
                        )}
                      >
                        {style.label}
                      </span>
                    </div>
                  );
                })}
              </CompareRow>
            ))}
          </div>
        );
      })}
    </>
  );
};

const GroupedMode = ({
  categories,
  playStylesAsList,
  players,
  gridStyle,
}: {
  categories: PsCategory[];
  playStylesAsList: PlayStyle[];
  players: DetailedPlayer[];
  gridStyle: React.CSSProperties;
}) => {
  const categoryNames = useSelector(playStyleCategoryNamesSelector);
  return (
    <>
      {categories
        // Filter before mapping so the zebra keeps alternating when a whole
        // category drops out.
        .map((category) => ({
          category,
          styles: ownedStyles(playStylesAsList, category, players),
        }))
        .filter(({ styles }) => styles.length > 0)
        .map(({ category, styles }, blockIndex) => (
          <CompareGroupedBlock
            key={category}
            label={categoryNames[category] ?? category}
            index={blockIndex}
            gridStyle={gridStyle}
            players={players}
            renderPlayer={(player) =>
              styles.map((style) => {
                const { hasPlus, hasNormal } = playStyleState(player, style.id);
                return (
                  <PlayStyleIcon
                    key={style.id}
                    id={style.id}
                    hasPlus={hasPlus}
                    hasNormal={hasNormal}
                  />
                );
              })
            }
          />
        ))}
    </>
  );
};

const CompactMode = ({
  playStylesAsList,
  players,
  gridStyle,
}: {
  playStylesAsList: PlayStyle[];
  players: DetailedPlayer[];
  gridStyle: React.CSSProperties;
}) => (
  <CompareCompactRow
    gridStyle={gridStyle}
    players={players}
    renderPlayer={(player) => {
      // Only playstyles the player owns, plus before normal — one dense
      // wrap per player, no category grouping.
      const owned = playStylesAsList
        .map((style) => ({ style, ...playStyleState(player, style.id) }))
        .filter(({ hasPlus, hasNormal }) => hasPlus || hasNormal)
        .sort((a, b) => Number(b.hasPlus) - Number(a.hasPlus));
      return owned.map(({ style, hasPlus, hasNormal }) => (
        <PlayStyleIcon
          key={style.id}
          id={style.id}
          hasPlus={hasPlus}
          hasNormal={hasNormal}
        />
      ));
    }}
  />
);

export const PlayStylesCompareSection = ({
  players,
  gridStyle,
  tiers,
  open,
  onToggle,
  mode,
  pinnedWidth,
}: {
  players: DetailedPlayer[];
  gridStyle: React.CSSProperties;
  /** Per-player `playStyles` tier + winner from the comparison endpoint. */
  tiers?: SectionTier[];
  open: boolean;
  onToggle: () => void;
  mode: Mode;
  /**
   * Mobile: sticky-left width for the section title and the category headers
   * inside the shared horizontal scroller.
   */
  pinnedWidth?: number;
}) => {
  const playStylesAsList = useSelector(playStylesSelector);
  const { t } = useTranslation("compare");
  const categories: PsCategory[] = [
    ...OUTFIELD_CATEGORIES,
    ...(anyGoalkeeper(players) ? (["Goalkeeper"] as const) : []),
  ];

  return (
    <CompareSectionShell
      title={t("categories.playStyles")}
      count={players.length}
      gridStyle={gridStyle}
      tiers={tiers}
      open={open}
      onToggle={onToggle}
      mode={mode}
      pinnedWidth={pinnedWidth}
      headerClassName="mt-3"
    >
      {mode === "list" ? (
        <ListMode
          categories={categories}
          playStylesAsList={playStylesAsList}
          players={players}
          gridStyle={gridStyle}
          pinnedWidth={pinnedWidth}
        />
      ) : mode === "grouped" ? (
        <GroupedMode
          categories={categories}
          playStylesAsList={playStylesAsList}
          players={players}
          gridStyle={gridStyle}
        />
      ) : (
        <CompactMode
          playStylesAsList={playStylesAsList}
          players={players}
          gridStyle={gridStyle}
        />
      )}
    </CompareSectionShell>
  );
};
